import React from 'react';
import * as constants from 'app-constants';
import { collection, doc, setDoc, serverTimestamp, Timestamp } from 'firebase/firestore';
import { useDropzone } from 'react-dropzone';
import { ref, uploadBytes } from 'firebase/storage';
import { useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import {
  canvasToBuffer,
  loadImageOnCanvas,
  ImageFormat,
  load,
} from 'services/utility/image/resizeImage';
import * as pipelines from 'services/utility/image/imageProcessingPipelines';
import { processImage } from 'services/utility/image/imageProcessor';
import { convertToPixelCrop, PercentCrop, PixelCrop } from 'react-image-crop';
import { ONLY_APPROVED_ARTISTS_QUERY, PUZZLE_IMAGE } from 'app-constants';
import { assertSchema } from 'validations';
import { useLiveData } from 'hooks/useLiveData';

import { firestore, storage, STORAGE_URL } from '../../../../services/Firebase';
import { Button, Input, SelectForm, Splash } from '../../../../shared/Components';
import { useForm } from '../../../../hooks';
import { strings } from '../../../../strings';
import { UploadField, uploadSchema } from '../../../../validations/uploadImage';
import { MODAL_TYPES, useGlobalModalContext } from '../../../../context/GlobalModal';
import { PuzzleDTO, PuzzleDTOSchema, PuzzleStatus } from '../../../../types';
import { getAllCollections } from '../../../../services';
import UploadImage from '../../../../shared/Components/UploadImage/UploadImage';
import {
  Box,
  DropZoneContainer,
  FlexContainer,
  IconCartContainer,
  Image,
  ImageContainer,
  Text,
  UploadedImageContainer,
  ImageResponsive,
} from '../../../../shared/Typhography';
import { RemoveIcon } from '../../../../assets/Modal';
import { centerAspectCrop } from '../../../../modals/Cropper/CropperModal';

import { AcceptedFilesTable } from './Shared';
import { checkIfIsGotivaArtist, getPuzzlePremiumTierLabel } from './utilities';
import usePremiumPuzzlePrices from './hooks/usePremiumPuzzles';

interface FormInputs {
  [UploadField.Name]: string;
  [UploadField.Keywords]: string;
  [UploadField.Collection]: { value: string; label: string; id: string };
  [UploadField.Image]: string;
  [UploadField.ArtistId]: { value: string; label: string };
  [UploadField.SecondaryCollection]: { value: string; label: string; id: string };
  [UploadField.AIGenerated]: boolean;
  [UploadField.PremiumTier]: { value: number; label: string };
}

export interface FileData {
  file: File;
  id: string;
  name: string;
  url: string;
  keywords: string;
  collection?: string;
  collectionId?: string;
  collectionIdFirebase?: string;
  type: string;
  original: string;
  collections?: string;
  validImage?: boolean;
  originalResolution: { width: number; height: number };
  hasHqImage: boolean;
  physicalDistribution?: boolean;
  aiGenerated?: boolean;
  /** Percent crop is most convenient while cropping is in process. */
  percentCropData: PercentCrop;
  /**
   * Pixel crop is necessary when uploading image to have precise data avoiding partial pixels.
   * and because we already save using pixel form in new artist panel.
   */
  pixelCropData: PixelCrop;
}

interface Image {
  url: string;
  file: File | null;
}

const formDefaultValues = {
  [UploadField.PremiumTier]: { value: 0, label: getPuzzlePremiumTierLabel(0) },
};

export default function CreatePuzzleScreen() {
  // Reference to currently edited image
  const image = React.useRef<Image>({ url: '', file: null });

  const currentFileExtension = React.useRef<string>('');
  const idEditPuzzle = React.useRef<string>('');

  // Main React Query hook.
  const queryClient = useQueryClient();

  // Used to show image crop modal
  const { showModal } = useGlobalModalContext();

  // Dropozone for image upload.
  const { getRootProps, getInputProps } = useDropzone({
    accept: 'image/jpeg,image/png',
    onDrop: getAllAcceptedFiles,
  });

  // For indicating loading stuff besides React Query requests.
  const [loading, setLoading] = React.useState<boolean>(false);

  // Used for upload image container as src attribute and for resizing images,
  // saved as base64 encoded string.
  const [src, setSrc] = React.useState<any>('');

  // Image files added via onDrop event.
  const [images, setImages] = React.useState<FileData[]>([]);

  // Data for react-select components.
  // So admin can pick to which puzzle collection and artist to assign image to.
  const { data: collectionsData, isLoading: collectionDataLoading } = getAllCollections();
  const [collections, setCollections] = React.useState<any>([]);
  const [artists, setArtists] = React.useState<any>([]);

  const { docs: artistsData, state: artistsFetchState } = useLiveData(ONLY_APPROVED_ARTISTS_QUERY);

  const { premiumPuzzlePricesDropdownList, isLoadingPremiumPuzzlePrices } =
    usePremiumPuzzlePrices();

  const artistsDataLoading = artistsFetchState === 'loading';

  // For handling user input.
  const {
    handleSubmit,
    control,
    setValue,
    resetField,
    reset,
    formState: { errors },
  } = useForm<FormInputs>({
    schema: uploadSchema,
    defaultValues: formDefaultValues,
  });

  React.useEffect(() => {
    getArtistsData();
  }, [artistsData]);

  React.useEffect(() => {
    getCollections();
  }, [collectionsData]);

  if (collectionDataLoading || artistsDataLoading || isLoadingPremiumPuzzlePrices) {
    return <Splash />;
  }

  const getResolution = async (imagUrl: string) => {
    const originalResolution = await getImageResolution(imagUrl);

    // EXMAPLE: First true
    const recommendedResolution = [
      {
        case:
          originalResolution.width >= constants.Puzzle2kImageSize &&
          originalResolution.height >= constants.Puzzle2kImageSize,
        value: constants.Puzzle2kImageSize,
      },
      { case: true, value: 0 },
    ].find((resoutionCase) => resoutionCase.case)?.value;

    return { original: originalResolution, image: recommendedResolution ?? 0 };
  };

  return (
    <Box margin="150px auto" boxSizing="content-box">
      <FlexContainer gap="3rem" flexDirection="column">
        <Text size={2} weight="bold">
          {strings.createPuzzleTitle}
        </Text>
        {src && images.length > 0 ? (
          <UploadedImageContainer
            onChange={handleSubmit(handleOnChangeForm)}
            onSubmit={handleSubmit(uploadImage)}
          >
            <FlexContainer
              gap="1rem"
              display="flex"
              alignItems="center"
              justifyContent="center"
              width="100%"
              flexWrap="wrap"
            >
              <ImageContainer flexDirection="column" justifyContent="flex-start" gap="1rem">
                <ImageResponsive
                  height="300px"
                  width="300px"
                  cursor="pointer"
                  onClick={cropperModal}
                  borderRadiusType="rounded"
                  src={src}
                  alt="Picture"
                  border={
                    !images.find((img) => img.id == idEditPuzzle.current)?.validImage
                      ? '#F12D40 solid 4px'
                      : undefined
                  }
                />
                <IconCartContainer onClick={() => deleteImage(idEditPuzzle.current)}>
                  <RemoveIcon />
                </IconCartContainer>
                <FlexContainer
                  gap="1rem"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  width="100%"
                  flexWrap="wrap"
                >
                  <Input
                    control={control}
                    type="checkbox"
                    width="25px"
                    defaultChecked={false}
                    defaultValue={false}
                    name={UploadField.AIGenerated}
                    placeholder={strings.aiGenerated}
                  />
                  <Text size={0.8} textAlign="center">
                    {'AI Generated'}
                  </Text>
                </FlexContainer>
                <FlexContainer justifyContent="center" {...getRootProps()}>
                  <input {...getInputProps()} />
                  <Button variant="link">{strings.change}</Button>
                </FlexContainer>

                <Text textAlign="center">
                  {images.find((img) => img.id == idEditPuzzle.current)?.originalResolution?.width}x
                  {images.find((img) => img.id == idEditPuzzle.current)?.originalResolution?.height}
                </Text>
              </ImageContainer>

              <FlexContainer
                flexDirection="column"
                justifyContent="space-between"
                flex="1"
                flexWrap="wrap"
                alignSelf="stretch"
              >
                <FlexContainer
                  flexDirection="row"
                  justifyContent="space-evenly"
                  flex="1"
                  gap="20px"
                >
                  <FlexContainer
                    flexDirection="column"
                    justifyContent="space-between"
                    minWidth="400px"
                    height="fit-content"
                    gap="33px"
                  >
                    <Input
                      label="Name"
                      required
                      control={control}
                      type="text"
                      width="300px"
                      name={UploadField.Name}
                      error={errors[UploadField.Name]}
                      minWidth="200px"
                    />
                    <SelectForm
                      required
                      width="300px"
                      placeholder={strings.selectArtist}
                      values={artists}
                      control={control}
                      name={UploadField.ArtistId}
                      error={errors[UploadField.ArtistId]}
                      label="Artist"
                      id="artist-select-dropdown"
                      includeCustomSearch
                    />
                  </FlexContainer>
                  <FlexContainer
                    flexDirection="column"
                    justifyContent="space-between"
                    height="fit-content"
                    gap="33px"
                  >
                    <SelectForm
                      width="300px"
                      required
                      placeholder={strings.selectCollection}
                      values={collections}
                      control={control}
                      name={UploadField.Collection}
                      error={errors[UploadField.Collection]}
                      label="Primary Category"
                      id="primary-collection-select-dropdown"
                    />
                    <SelectForm
                      width="300px"
                      required
                      placeholder={strings.selectCollection}
                      values={collections}
                      control={control}
                      name={UploadField.SecondaryCollection}
                      isClearable={true}
                      label="Secondary Category"
                      id="secondary-collection-select-dropdown"
                    />
                  </FlexContainer>
                </FlexContainer>
                <FlexContainer
                  flexDirection="row"
                  flex="1"
                  justifyContent="space-evenly"
                  gap="30px"
                >
                  <SelectForm
                    required
                    width="300px"
                    placeholder={strings.selectPuzzleTier}
                    values={premiumPuzzlePricesDropdownList}
                    control={control}
                    name={UploadField.PremiumTier}
                    label="Price"
                    id="tier-select-dropdown"
                    margin="33px 0 0 5px"
                  />
                  <div style={{ width: 300 }}>{/* Placeholder for next option */}</div>
                </FlexContainer>
                <FlexContainer
                  flexDirection="row"
                  justifyContent="center"
                  height="fit-content"
                  margin="30px 0"
                >
                  <Input
                    label="Keywords"
                    required
                    control={control}
                    type="textarea"
                    outerWidth="70%"
                    height="120px"
                    placeholder={UploadField.Keywords}
                    name={UploadField.Keywords}
                    error={errors[UploadField.Keywords]}
                  />
                </FlexContainer>
                <FlexContainer
                  width="100%"
                  flexDirection="row"
                  justifyContent="center"
                  gap="30px"
                  margin="20px 0 0 0"
                >
                  <Button variant="link" onClick={discard} height={40} width={140} margin="10px">
                    {strings.discard}
                  </Button>
                  <Button disabled={loading} height={40} width={140} type="submit" margin="10px">
                    {!loading ? (
                      strings.uploadImage
                    ) : (
                      <Splash color="white" height={25} width={25} />
                    )}
                  </Button>
                </FlexContainer>
              </FlexContainer>
            </FlexContainer>
          </UploadedImageContainer>
        ) : (
          <DropZoneContainer {...getRootProps()}>
            <input {...getInputProps()} />
            <UploadImage buttonText={strings.uploadImage} />
          </DropZoneContainer>
        )}
        {images.length > 0 && (
          <AcceptedFilesTable
            removePuzzle={deleteImage}
            editPuzzle={editPuzzle}
            dataTable={images}
          />
        )}
      </FlexContainer>
    </Box>
  );

  /**
   * This function will trigger automatic change of image in images table after we select image.
   * @param imageData New image data to replace the old one with
   */
  function updateImage(imageData: FileData) {
    image.current.url = imageData.url;
    setSrc(imageData.url);

    setImages((prevImages) => {
      return prevImages.map((image) => {
        return image.id === imageData.id ? imageData : image;
      });
    });
  }

  /**
   * Function for open cropper modal.
   */
  function cropperModal() {
    const image = images.find((image) => image.id === idEditPuzzle.current);

    showModal(MODAL_TYPES.CROPPER_MODAL, {
      img: image?.original,
      handleSubmit: updateImage,
      title: strings.cropperModalTitleGamePreview,
      status: PuzzleStatus.PENDING_UPLOAD,
      imageData: image,
    });
  }

  /**
   * This function is set onChange form for changing field of images(name,keywords,collection) in temporary table(images to upload).
   */
  function handleOnChangeForm(data: FormInputs) {
    const file = images.find((image) => image.id === idEditPuzzle.current);

    if (file) {
      file.name = data.name;
      file.keywords = data.keywords;
      file.collection = data.collection.label;
      file.collectionId = data.collection.value;
      file.collectionIdFirebase = data.collection.id;
      file.aiGenerated = data.aiGenerated;
    }
  }

  function getCollections() {
    const finalData = collectionsData?.docs
      ?.map((item) => ({
        value: item.data().id,
        label: item.data().name,
        id: item.id,
      }))
      .sort((a, b) => {
        if (a.label.toLowerCase() < b.label.toLowerCase()) return -1;
        if (a.label.toLowerCase() > b.label.toLowerCase()) return 1;
        return 0;
      });
    setCollections(finalData);
  }

  function getArtistsData() {
    const finalData = artistsData?.map((item) => ({
      value: item.id,
      label: (
        <FlexContainer gap="1rem" alignItems="center" flexDirection="row">
          <Image size="profileImage" borderRadiusType="circle" src={item.data().profileImageUrl} />
          {item.data().Name}
        </FlexContainer>
      ),
      searchValue: item.data().Name, // For simpler custom search functionality.
    }));
    setArtists(finalData);
  }

  /**
   * Uploads image to firebase storage.
   *
   * When upload is finished it will triger firebase cloud function for
   * resizing which creates puzzle and icon versions of image.
   * Cloud function name: imageUploadedTrigger()
   *
   * Cloud function update documents _meta field with
   *
   * @param data
   */
  async function uploadImage(data: FormInputs) {
    setLoading(true);

    if (data?.collection?.id === data?.secondaryCollection?.id) {
      toast.error('Puzzle can not have two same categories!');
      setLoading(false);
      return;
    }

    const Publisher = data?.Publisher?.value
      ? data.Publisher.value
      : constants.GOTIVA_ARTIST_DOC_ID;
    const premiumTier = data?.premiumTier?.value ?? 0;

    //Delete this "if" when needed since this check is temporarily for now.
    if (premiumTier && !checkIfIsGotivaArtist(Publisher)) {
      toast.error('Not possible to set non-Gotiva puzzle as Premium.');
      setLoading(false);
      return;
    }

    try {
      const puzzlesRef = collection(firestore, 'puzzles');
      const puzzleDoc = doc(puzzlesRef);
      const id = puzzleDoc.id;

      const originalData = images.find((img) => img?.id == idEditPuzzle?.current);

      if (!originalData) {
        throw 'No original data';
      }

      const original = originalData.file;
      if (!original) {
        throw 'Failed to find original image';
      }

      const uploadFromCanvas = async (
        canvas: HTMLCanvasElement | ArrayBuffer,
        assetName: string,
        imageFormat: ImageFormat,
        quality = 1,
        type = 'canvas',
      ): Promise<string> => {
        const buffer =
          type === 'canvas'
            ? await canvasToBuffer(canvas as HTMLCanvasElement, imageFormat, quality)
            : (canvas as ArrayBuffer);

        const result = await uploadImageToStorage(`${id}/${assetName}`, buffer, imageFormat);
        if (!result) {
          throw 'Failed to upload asset ${assetName}';
        }
        return result.reference.ref.fullPath;
      };

      const uploadFile = async (file: File, assetName: string): Promise<string> => {
        const result = await uploadImageToStorage(`${id}/${assetName}`, file);
        if (!result) {
          throw 'Failed to upload asset ${assetName}';
        }
        return result.reference.ref.fullPath;
      };

      const resolutionMap = await getResolution(image.current.url);

      if (resolutionMap.image == 0) {
        setImages(
          images.map((img) =>
            img?.id == idEditPuzzle?.current ? { ...img, validImage: false } : img,
          ),
        );
        toast.error('Image resolution is too low for usage!');
        setLoading(false);
        return;
      }

      const results = await Promise.all([
        (async () => {
          return uploadFile(original, 'original');
        })(),
        (async () => {
          const iconCanvas = await resizeImage(
            image.current.url,
            constants.PuzzleIconSize,
            constants.PuzzleIconSize,
          );
          return uploadFromCanvas(
            iconCanvas.data,
            'icon',
            ImageFormat.JPEG,
            constants.JPEGQualityHigh,
            iconCanvas.type,
          );
        })(),
        (async () => {
          const imageCanvas = await resizeImage(
            image.current.url,
            resolutionMap.image,
            resolutionMap.image,
          );
          return uploadFromCanvas(
            imageCanvas.data,
            'image',
            ImageFormat.JPEG,
            constants.JPEGQualityHigh,
            imageCanvas.type,
          );
        })(),
        (async () => {
          const imageCanvas = await resizeImage(
            image.current.url,
            PUZZLE_IMAGE.DIGITAL.CROP_LQ.WIDTH,
            PUZZLE_IMAGE.DIGITAL.CROP_LQ.HEIGHT,
          );

          return uploadFromCanvas(
            imageCanvas.data,
            'image_lq',
            ImageFormat.JPEG,
            constants.JPEGQualityHigh,
            imageCanvas.type,
          );
        })(),
        (async () => {
          if (!originalData.hasHqImage) {
            return null;
          }

          const imageCanvas = await resizeImage(
            image.current.url,
            PUZZLE_IMAGE.DIGITAL.CROP_HQ.WIDTH,
            PUZZLE_IMAGE.DIGITAL.CROP_HQ.HEIGHT,
          );

          return uploadFromCanvas(
            imageCanvas.data,
            'image_hq',
            ImageFormat.JPEG,
            constants.JPEGQualityHigh,
            imageCanvas.type,
          );
        })(),
      ]);

      const originalPath = results[0];
      const iconPath = results[1];
      const imagePath = results[2];
      const imageLqPath = results[3];
      const imageHqPath = results[4];

      const timestamp = serverTimestamp() as Timestamp;

      // Create puzzle document
      const puzzleData: PuzzleDTO = {
        Publisher,
        name: data.name,
        type: 'static',
        keywords: data.keywords.toUpperCase(),
        status: PuzzleStatus.APPROVED,
        createdAt: timestamp,
        // We want to define this immediately as puzzles created from admin panel are approved by default.
        publishedAt: timestamp,
        printUrl: null,
        iconURL: `${STORAGE_URL}/${iconPath}?timestamp=${new Date().getTime()}`,
        url: `${STORAGE_URL}/${imagePath}?timestamp=${new Date().getTime()}`,
        highQualityImageUrl: originalData.hasHqImage
          ? `${STORAGE_URL}/${imageHqPath}?timestamp=${new Date().getTime()}`
          : null,
        lowQualityImageUrl: `${STORAGE_URL}/${imageLqPath}?timestamp=${new Date().getTime()}`,
        _meta: {
          original: originalPath,
          resolution: `${resolutionMap.image}x${resolutionMap.image}`,
          originalResolution: `${originalData.originalResolution.width}x${originalData.originalResolution.height}`,
          highQualityResolution: originalData.hasHqImage
            ? `${PUZZLE_IMAGE.DIGITAL.CROP_HQ.WIDTH}x${PUZZLE_IMAGE.DIGITAL.CROP_HQ.HEIGHT}`
            : null,
          lowQualityResolution: `${PUZZLE_IMAGE.DIGITAL.CROP_LQ.WIDTH}x${PUZZLE_IMAGE.DIGITAL.CROP_LQ.HEIGHT}`,
          digitalCropData: {
            x: originalData.pixelCropData.x,
            y: originalData.pixelCropData.y,
            width: originalData.pixelCropData.width,
            height: originalData.pixelCropData.height,
            rotation: 0,
          },
        },
        collections: [data.collection.id, data.secondaryCollection?.id].filter((value) => !!value),
        physicalDistribution: false, //Backward compatibility with Flutter. Don't remove even if it's not in use.
        aiGenerated: data?.aiGenerated,
        plays: 0,
        likes: 0,
        premiumTier,
      };

      try {
        assertSchema<PuzzleDTO>(puzzleData, PuzzleDTOSchema);
      } catch (error: any) {
        toast.error('Something is wrong with puzzle data: ' + error.message);
        console.log(error);
        setLoading(false);
        return;
      }

      // Save puzzle document to database to get the reference with unique id.
      await setDoc(puzzleDoc, { uuid: id, ...puzzleData });

      queryClient.invalidateQueries('puzzlesOrderByCollectionId');
      reset();
      setSrc('');
      deleteImage(idEditPuzzle.current);

      toast.success(strings.toastSuccessfullyUploadedImage);
    } catch (error) {
      if (typeof error == 'string') toast.error(error);
      else toast.error(strings.toastNotificationSomethingWrong);
      setLoading(false);
    }

    setLoading(false);
  }

  async function resizeImage(image: string, width: number, height: number) {
    const canvas = await loadImageOnCanvas(image);
    const processedImage = await processImage(
      pipelines.resizeAndCropToKeepAspect,
      canvas,
      width,
      height,
    );

    try {
      const canvasBlob: ArrayBuffer = await new Promise((resolve, reject) => {
        processedImage.toBlob(async (blob) => {
          if (blob) {
            resolve(await blob.arrayBuffer());
          } else {
            reject(new Error('Failed to build file'));
          }
        }, 'image/jpeg');
      });

      return { data: canvasBlob, type: 'buffer' };
    } catch (err) {
      console.log(err);
    }

    return { data: processedImage, type: 'canvas' };
  }

  async function getImageResolution(loadImage: string) {
    const loadedImage = await load(loadImage);
    return { width: loadedImage.width, height: loadedImage.height };
  }

  async function uploadImageToStorage(
    name: string,
    data: ArrayBuffer | File,
    imageFormat?: ImageFormat,
  ) {
    try {
      if (!data) {
        toast.error('Image not selected');
        return;
      }

      const imageReference = ref(storage, `puzzles/${name}`);
      const metadata = typeof imageFormat !== 'undefined' ? { contentType: imageFormat } : {};
      const imageResult = await uploadBytes(imageReference, data, metadata);

      return {
        success: true,
        reference: imageResult,
      };
    } catch (error) {
      toast.error(strings.somethingWentWrong);
    }
  }

  function deleteImage(id: string) {
    try {
      const copyFiles = [...images.filter((item) => item.id !== id)];
      setImages(copyFiles);

      if (copyFiles.length) {
        editPuzzle(copyFiles[0]);
      }
    } catch (error) {
      toast.error(strings.toastNotificationSomethingWrong);
    }
  }

  /**
   * Function for set values in form with fields of this image(row in table).
   */
  function editPuzzle(data: FileData) {
    currentFileExtension.current = data.type;
    setValue(UploadField.Name, data.name);
    setValue(UploadField.Keywords, data.keywords);
    setSrc(data.url);
    image.current.url = data.url;
    image.current.file = data.file;
    idEditPuzzle.current = data.id;
    resetField(UploadField.Collection);
  }

  function discard() {
    idEditPuzzle.current = '';
    setSrc('');
    image.current.url = '';
    image.current.file = null;
    reset();
  }

  /**
   * Function for get all images from react-dropzone, and set state image with that images.
   * Retrieves images from dropzone and sets them in a
   */
  async function getAllAcceptedFiles(acceptedFiles: File[]) {
    try {
      const mappedFiles = await Promise.all(
        acceptedFiles.map(async (item): Promise<FileData> => {
          const fileType = item.type.replace('image/', '');
          const name = cleanupImageName(item.name);
          const imageUrl = URL.createObjectURL(item);
          const validResolution = await getResolution(imageUrl);
          const originalResolution = validResolution.original;

          const initialPercentCrop = centerAspectCrop(
            originalResolution.width,
            originalResolution.height,
            1,
          );

          const initialPixelCrop = convertToPixelCrop(
            initialPercentCrop,
            originalResolution.width,
            originalResolution.height,
          );

          return {
            file: item,
            name: name,
            url: imageUrl,
            id: generateUniqueId(),
            keywords: '',
            type: fileType,
            original: imageUrl,
            validImage: validResolution?.image != 0,
            originalResolution: validResolution.original,
            percentCropData: initialPercentCrop,
            hasHqImage: initialPixelCrop.width >= PUZZLE_IMAGE.DIGITAL.CROP_HQ.WIDTH,
            pixelCropData: {
              x: Math.round(initialPixelCrop.x),
              y: Math.round(initialPixelCrop.y),
              width: Math.round(initialPixelCrop.width),
              height: Math.round(initialPixelCrop.height),
              unit: initialPixelCrop.unit,
            },
          };
        }),
      );

      editPuzzle(mappedFiles[0]);

      setImages((prevValue: FileData[]) => [...prevValue, ...mappedFiles]);
    } catch (error) {
      toast.error(strings.toastNotificationSomethingWrong);
    }
  }

  /**
   * When puzzle is added its name can contain unwanted characters,
   * and file extension, which mostly get deleted anyway.
   * This function removes that for easier editing.
   */
  function cleanupImageName(name: string): string {
    // Remove file type from image
    name = name.replace(/\.\w*$/i, '');

    // Replace dots, underscores and dashes with spaces
    name = name.replace(/[-,_.]/gi, ' ');

    return name;
  }

  /**
   * Generates quasi-unique id, which is sufficient for this component,
   * in the end only used as for image id, which in turn is used as key prop for table.
   */
  function generateUniqueId() {
    const timestamp = new Date().getTime();
    const randomInt = Math.random().toString().replace('0.', '');

    return `${timestamp}_${randomInt}`;
  }
}
